import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';

import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Input,
  Link as MuiLink,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as CreateIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

import { CreateAddressModal } from './components';
import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';
import { Filter } from '../../components/filter/filter';
import { FilterField } from '../../components/filter/types';

import {
  CreateTransitAddressDTO,
  GetBlockchainBalanceResult,
  useCreateTransitAddressMutation,
  useGetTransitAddressesQuery,
  useLazyGetBlockchainBalanceQuery,
} from '../../api';
import { useAppActions } from '../../utils/hooks';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import type { TransitAddress } from '../../types';
import { RouterPaths } from '../../router/paths';
import { Link } from 'react-router-dom';
import { permissionsCheck } from '../../utils/permission';
import { formatBlockchainAmountWithCurrency } from '../../utils';

export const TransitAddresses: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditTransitAddresses = permissionsCheck(['WRITE_TRANSIT_ADDRESSES']);

  // api
  const {
    data: transitAddresses,
    isLoading: isTransitAddressesLoading,
    isFetching: isTransitAddressesFetching,
  } = useGetTransitAddressesQuery();
  const [createAddress, { isLoading: isCreateAddressLoading }] = useCreateTransitAddressMutation();
  const [
    getBlockchainBalance,
    {
      isLoading: isBalanceGetLoading,
      isFetching: isBalanceGetFetching,
      originalArgs: originalBalanceArgs,
    },
  ] = useLazyGetBlockchainBalanceQuery();

  // variables
  const searchRef = useRef<HTMLInputElement>(null);
  const [filteredAddresses, setFilteredAddresses] = useState<TransitAddress[] | undefined>([]);
  const isLoading = isTransitAddressesLoading || isCreateAddressLoading || isBalanceGetLoading;
  const [blockchainBalances, setBlockchainBalances] = useState<Record<
    string,
    GetBlockchainBalanceResult
  > | null>(null);

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditTransitAddresses &&
      setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  // set addresses to local state for filtering
  useEffect(() => {
    if (isLoading || isTransitAddressesFetching) return;
    if (!transitAddresses) return;

    const searchTerm = searchRef.current?.value;
    if (searchTerm) {
      if (searchTerm === '') setFilteredAddresses(transitAddresses.addresses);
      const filtered = transitAddresses.addresses.filter((address) => {
        return (
          address.coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          address.blockchain_address.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredAddresses(filtered);
    } else {
      setFilteredAddresses(transitAddresses.addresses);
    }
  }, [transitAddresses, isLoading, isTransitAddressesFetching, searchRef.current?.value]);

  // fields logic
  const [fields, setFields] = useState<CreateTransitAddressDTO['body'] | null>(null);
  const handleFields = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name as keyof CreateTransitAddressDTO['body'];
    const value = e.target.value;

    // @ts-ignore
    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // validation for required fields
  const requiredFields = ['coin_id'] as (keyof CreateTransitAddressDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    return false;
  };

  // create modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setFields(null);
  };

  // create transit address handler
  const createTransitAddress = async () => {
    if (!fields) return;

    const args = {
      body: {
        coin_id: fields.coin_id,
        address: fields?.address ? fields.address : undefined,
      },
    };

    try {
      await createAddress(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // get blockchain balance handler
  const getBalance = async (addressId: number) => {
    if (!addressId) return;

    const args = { addressId };

    try {
      const res = await getBlockchainBalance(args).unwrap();
      setBlockchainBalances((prevState) => ({
        ...prevState,
        [addressId]: { ...res },
      }));
    } catch {
      // @ts-ignore
      enqueueSnackbar(t('transit-addresses.show-balance-error'), { variant: 'error' });
    }
  };

  // filter logic
  const handleSearchValue = () => {
    if (!transitAddresses || !searchRef.current) return;

    const searchTerm = searchRef.current.value;

    if (searchTerm === '') setFilteredAddresses(transitAddresses.addresses);
    const filtered = transitAddresses.addresses.filter((address) => {
      return (
        address.coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        address.blockchain_address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setFilteredAddresses(filtered);
  };

  const debouncedSearchHandler = useDebouncedCallback(() => {
    handleSearchValue();
  }, 600);

  const filterFields: FilterField[] = [
    {
      name: 'search',
      title: t('transit-addresses.filter.common-search'),
      component: (
        <Input
          inputRef={searchRef}
          name="search"
          onChange={debouncedSearchHandler}
          sx={{ minWidth: '280px', maxWidth: '360px' }}
          fullWidth
        />
      ),
    },
  ];

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('transit-addresses.head-cells.coin') },
    { id: '2', label: t('transit-addresses.head-cells.blockchain-address') },
    { id: '3', label: t('transit-addresses.head-cells.actions') },
  ];

  const rows: Row[] | undefined = filteredAddresses?.map((it) => {
    const { id, coin, blockchain_address } = it;

    const cells: RowCell[] = [
      {
        name: 'coin_name',
        sx: { cursor: 'pointer' },
        component: (
          <MuiLink underline="hover" component={Link} to={`${RouterPaths.TransitAddresses}/${id}`}>
            <Typography>{coin.name}</Typography>
          </MuiLink>
        ),
      },
      { name: 'blockchain_address', value: blockchain_address },
      {
        name: 'actions',
        component: (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Tooltip title={t('transit-addresses.show-balance')}>
              <IconButton
                aria-label="show-balance"
                color="info"
                onClick={() => getBalance(id)}
                disabled={isBalanceGetLoading || isBalanceGetFetching}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>

            {(isBalanceGetLoading || isBalanceGetFetching) &&
            originalBalanceArgs?.addressId === id ? (
              <CircularProgress size={24} />
            ) : // eslint-disable-next-line no-prototype-builtins
            blockchainBalances?.hasOwnProperty(id) ? (
              <Stack direction="column">
                <Box>
                  <Box component="span" fontWeight={600}>
                    {formatBlockchainAmountWithCurrency(
                      blockchainBalances[id].balance,
                      blockchainBalances[id].coin.code
                    )}
                  </Box>
                </Box>

                <Box>
                  <Box component="span" fontWeight={600}>
                    {formatBlockchainAmountWithCurrency(
                      blockchainBalances[id].fee_coin_balance,
                      blockchainBalances[id].fee_coin_code
                    )}
                  </Box>
                </Box>
              </Stack>
            ) : null}
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Filter filterFields={filterFields} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateAddressModal
        open={createModalShown}
        onClose={closeCreateModal}
        onSuccess={createTransitAddress}
        onCancel={closeCreateModal}
        isLoading={isLoading}
        fields={fields}
        fieldsHandler={handleFields}
        submitDisabling={checkSubmitDisabling()}
      />
    </>
  );
};
